import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Shop-the-look builder. Every piece is a row with an include checkbox and, when it has options,
 * a variant select. The summary keeps the piece count, subtotal and the look-saving status in
 * step, and "Add the look" sends every included variant to the cart in one request, then tells
 * the cart drawer and cart icon through the standard CartLinesUpdateEvent.
 *
 * Without JavaScript the form still posts every piece to /cart/add.
 *
 * @typedef {object} LookBuilderRefs
 * @property {HTMLFormElement} form
 * @property {HTMLElement[]} rows
 * @property {HTMLElement} count
 * @property {HTMLElement} subtotal
 * @property {HTMLElement} [savingStatus]
 * @property {HTMLButtonElement} submit
 * @property {HTMLElement} submitLabel
 * @property {HTMLElement} status
 * @property {HTMLTemplateElement} [moneyFormat]
 *
 * @extends Component<LookBuilderRefs>
 */
class LookBuilder extends Component {
  requiredRefs = ['form', 'count', 'subtotal', 'submit', 'submitLabel', 'status'];

  /** @type {AbortController | null} */
  #request = null;

  connectedCallback() {
    super.connectedCallback();
    this.update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#request?.abort();
  }

  /** The included rows that resolve to a purchasable variant. */
  get #picks() {
    const picks = [];
    for (const row of this.refs.rows ?? []) {
      const include = row.querySelector('[data-role="include"]');
      const variant = row.querySelector('[data-role="variant"]');
      if (!(include instanceof HTMLInputElement) || !variant) continue;

      const included = include.checked && !include.disabled;
      row.classList.toggle('is-excluded', !included);
      if (!included) continue;

      const source =
        variant instanceof HTMLSelectElement ? variant.selectedOptions[0] : /** @type {HTMLElement} */ (variant);
      if (!source || (source instanceof HTMLOptionElement && source.disabled)) continue;

      picks.push({ id: Number(/** @type {HTMLInputElement} */ (variant).value), price: Number(source.dataset.price) });
    }
    return picks;
  }

  #money(cents) {
    const format = this.refs.moneyFormat?.content.textContent?.trim() || '${{amount}}';
    return formatMoney(cents, format, this.dataset.currency || 'USD');
  }

  /** Recomputes the summary. Bound to the form's change event. */
  update = () => {
    const picks = this.#picks;
    const count = picks.length;
    const subtotal = picks.reduce((sum, pick) => sum + pick.price, 0);
    const minPieces = Number(this.dataset.minPieces) || 0;
    const saving = this.dataset.saving;

    this.refs.count.textContent = `${count} ${count === 1 ? 'piece' : 'pieces'}`;
    this.refs.subtotal.textContent = this.#money(subtotal);
    this.refs.submitLabel.textContent = count ? `Add the look · ${count}` : 'Choose at least one piece';
    this.refs.submit.disabled = count === 0;

    if (this.refs.savingStatus && saving) {
      const shortBy = minPieces - count;
      this.refs.savingStatus.textContent =
        shortBy <= 0
          ? `Save ${saving}% at checkout`
          : `Add ${shortBy} more ${shortBy === 1 ? 'piece' : 'pieces'} to save ${saving}%`;
    }
  };

  /** @param {SubmitEvent} event */
  handleSubmit = async (event) => {
    event.preventDefault();
    const picks = this.#picks;
    if (picks.length === 0 || this.refs.submit.getAttribute('aria-busy') === 'true') return;

    this.#request?.abort();
    this.#request = new AbortController();
    this.#setBusy(true);
    this.refs.status.textContent = '';

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: picks.map((pick) => ({ merchandiseId: String(pick.id), quantity: 1 })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: picks.map((pick) => ({ id: pick.id, quantity: 1 })),
          sections: sectionIds.join(','),
        }),
        signal: this.#request.signal,
      });
      const result = await response.json();
      const cart = await this.#fetchCart();

      if (result.status) {
        this.refs.status.textContent = result.description || result.message || 'Something went wrong. Please try again.';
        this.dispatchEvent(new CartErrorEvent({ error: result.message || 'Add to cart failed', code: 'INVALID' }));
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'sg-look-builder', itemCount: picks.length },
        });
        return;
      }

      this.refs.status.textContent = `Added ${picks.length} ${picks.length === 1 ? 'piece' : 'pieces'} to your bag.`;
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          source: 'sg-look-builder',
          itemCount: picks.length,
          sections: result.sections,
        },
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error(error);
      deferred.reject(error);
      this.refs.status.textContent = 'We couldn’t reach the cart. Please try again.';
      this.dispatchEvent(new CartErrorEvent({ error: 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' }));
    } finally {
      this.#setBusy(false);
    }
  };

  async #fetchCart() {
    const cartItems = document.querySelector('cart-items-component');
    if (cartItems && 'fetchCartData' in cartItems) {
      await customElements.whenDefined('cart-items-component');
      return /** @type {any} */ (cartItems).fetchCartData();
    }
    const response = await fetch(`${Theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' } });
    return response.json();
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.refs.submit.setAttribute('aria-busy', String(busy));
    this.refs.submit.disabled = busy || this.#picks.length === 0;
    if (busy) this.refs.submitLabel.textContent = 'Adding…';
    else this.update();
  }
}

if (!customElements.get('sg-look-builder')) {
  customElements.define('sg-look-builder', LookBuilder);
}
